import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LEAD_SECTIONS } from "@/lib/leadSchema";
import { Loader2, Eye, Edit3, EyeOff, RotateCcw, History } from "lucide-react";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

const PAGES_LIST = [
  { key: "dashboard", label: "Dashboard" },
  { key: "leads", label: "Leads (admin table · all rows)" },
  { key: "new_lead", label: "Add Lead (create new lead form)" },
  { key: "master_exclusion", label: "Master Exclusion" },
  { key: "campaign_exclusion", label: "Campaign Exclusion" },
  { key: "bulk_upload", label: "Bulk Upload" },
  // Directly-visible tick so admins can grant Leaves-approval powers
  // (Approvals + Quotas + Holidays tabs) to HR / HR Manager / Sr. HR /
  // any role without opening the Admin-tabs dropdown. Backend gate
  // lives in `leaves_module.py::require_admin_subpage("admin_leaves")`.
  { key: "admin_leaves", label: "Leaves (Approvals + Quotas + Holidays)" },
  // Team Meetings tab access — schedule/edit/delete team meetings +
  // trainings, mark attendance. Typical grantees: HR, HR Manager,
  // Team Lead, QA Lead. Backend gate lives in
  // `team_meetings_module.py::_require_meeting_manager`.
  { key: "manage_team_meetings", label: "Team Meetings (schedule + attendance)" },
];

// Admin-panel sub-pages — shown in a single multi-select dropdown below the
// page-access checkboxes so admins can fine-grain which Admin tabs each role
// can open without cluttering the dialog with extra checkboxes.
const ADMIN_TAB_OPTIONS = [
  { value: "admin_campaigns", label: "Campaigns" },
  { value: "admin_agents", label: "Agents" },
  { value: "admin_users", label: "Users & Permissions" },
  { value: "admin_shifts", label: "Shifts" },
  { value: "admin_exclusion_lists", label: "Exclusion Lists" },
  { value: "admin_email_campaigns", label: "Email Campaigns" },
  { value: "admin_productivity", label: "Productivity (Edit)" },
  { value: "admin_productivity_view", label: "Productivity (View Only)" },
  { value: "admin_reports", label: "Reports & Analytics" },
  { value: "admin_shift_schedules", label: "Shift Schedules & App Freeze" },
];

// Feature-flag toggles (not pages, but per-role capability switches that
// affect what UI controls/actions the user can see). Rendered as their
// own checkbox row below the Admin-tabs dropdown.
const FEATURE_FLAGS = [
  { key: "export_leads", label: "Export leads to Excel (Leads → Export button)" },
  { key: "view_leaves_report", label: "View Leaves Report tab (HR / TL dashboard)" },
  // Bulk-assign toggle on the /leads page — hides/shows the whole
  // "Bulk Assign" collapsible panel and gates the backend endpoint.
  // Typically enabled for Team Lead so TLs can hand out lead queues.
  { key: "assign_leads", label: "Bulk-assign leads on the Leads page (Team Lead use case)" },
];

const LEVELS = [
  { value: "edit", label: "Edit", icon: Edit3, tone: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-emerald-300" },
  { value: "view", label: "View", icon: Eye, tone: "bg-sky-100 text-sky-800 hover:bg-sky-200 border-sky-300" },
  { value: "hidden", label: "Hidden", icon: EyeOff, tone: "bg-rose-100 text-rose-800 hover:bg-rose-200 border-rose-300" },
];

const TONE_FOR = {
  edit: "border-emerald-400 bg-emerald-50",
  view: "border-sky-400 bg-sky-50",
  hidden: "border-rose-400 bg-rose-50",
};

export default function RolePermissionsDialog({ role, open, onClose, onSaved }) {
  const [perms, setPerms] = useState({});
  const [defaults, setDefaults] = useState({});
  const [pagePerms, setPagePerms] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const reloadPermissions = async () => {
    if (!role) return;
    const [fp, pp] = await Promise.all([
      api.get(`/permissions/role/${role.id}`),
      api.get(`/permissions/role/${role.id}/pages`),
    ]);
    setPerms(fp.data.permissions || {});
    setDefaults(fp.data.base_defaults || {});
    setPagePerms(pp.data.page_permissions || {});
  };

  const loadHistory = async () => {
    if (!role) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/roles/${role.id}/permission-history?limit=50`);
      setHistory(r.data.history || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreSnapshot = async (snap) => {
    const ts = new Date(snap.ts).toLocaleString();
    if (!window.confirm(
      `Restore ${snap.kind === "page" ? "page" : "field"} permissions from ${ts}?\n\n` +
      `This will replace the current ${snap.kind === "page" ? "page-access" : "field-level"} permissions ` +
      `(${snap.after_count} keys) with the snapshot's previous state (${snap.before_count} keys).\n\n` +
      `The current state will itself be snapshotted, so this restore is reversible.`
    )) return;
    setRestoringId(snap.id);
    try {
      await api.post(`/roles/${role.id}/permission-restore/${snap.id}`);
      toast.success(`Restored ${snap.kind} permissions from ${ts}`);
      await Promise.all([reloadPermissions(), loadHistory()]);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  useEffect(() => {
    if (!role || !open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [fp, pp] = await Promise.all([
          api.get(`/permissions/role/${role.id}`),
          api.get(`/permissions/role/${role.id}/pages`),
        ]);
        if (!cancelled) {
          setPerms(fp.data.permissions || {});
          setDefaults(fp.data.base_defaults || {});
          setPagePerms(pp.data.page_permissions || {});
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load permissions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [role, open]);

  useEffect(() => {
    if (historyOpen && role) loadHistory();
  }, [historyOpen, role?.id]);

  const sections = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return LEAD_SECTIONS.map((s) => ({
      ...s,
      fields: s.fields.filter((field) =>
        !f || field.name.toLowerCase().includes(f) || field.label.toLowerCase().includes(f)
      ),
    })).filter((s) => s.fields.length > 0);
  }, [filter]);

  const counts = useMemo(() => {
    const c = { edit: 0, view: 0, hidden: 0 };
    Object.values(perms).forEach((v) => { if (c[v] !== undefined) c[v]++; });
    return c;
  }, [perms]);

  const setAll = (value) => {
    const next = {};
    Object.keys(perms).forEach((k) => { next[k] = value; });
    setPerms(next);
  };
  const resetToBase = () => setPerms({ ...defaults });

  const save = async () => {
    setSaving(true);
    try {
      // First attempt: normal save. Backend guards against accidental
      // wipes by returning 409 if the new permission set shrinks
      // dramatically (e.g. 49 → 2 keys). On 409 we ask the admin to
      // confirm, then retry with `?force=true`.
      const attempt = async (force) => {
        const qs = force ? "?force=true" : "";
        return Promise.all([
          api.put(`/roles/${role.id}/permissions${qs}`, { permissions: perms }),
          api.put(`/roles/${role.id}/page-permissions${qs}`, { pages: pagePerms }),
        ]);
      };
      try {
        await attempt(false);
      } catch (e) {
        if (e.response?.status === 409) {
          const msg = formatApiErrorDetail(e.response?.data?.detail) ||
            "This save would wipe most permissions for this role.";
          if (!window.confirm(`${msg}\n\nProceed anyway? (Click Cancel to keep the previous permissions.)`)) {
            toast.message("Save cancelled. Previous permissions preserved.");
            setSaving(false);
            return;
          }
          await attempt(true);
        } else {
          throw e;
        }
      }
      toast.success(`Permissions saved for ${role.name}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!role) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="role-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Field permissions · <span className="font-mono">{role.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm badge-info">base: {role.base_role}</span>
          </DialogTitle>
          <DialogDescription>
            For each lead-form field, choose whether this role can <strong>Edit</strong>, only <strong>View</strong>, or has the field completely <strong>Hidden</strong>. Defaults inherit from the permission base.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 items-center text-xs border-b border-border pb-3">
          <Input
            data-testid="perm-search"
            placeholder="Filter fields…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-sm h-8 w-48"
          />
          <span className="text-muted-foreground">Quick set:</span>
          {LEVELS.map((l) => (
            <button
              key={l.value}
              type="button"
              onClick={() => setAll(l.value)}
              data-testid={`perm-set-all-${l.value}`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-xs ${l.tone}`}
            >
              <l.icon size={11} /> All {l.label}
            </button>
          ))}
          <button
            type="button"
            onClick={resetToBase}
            data-testid="perm-reset-base"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-border text-xs hover:bg-secondary"
          >
            <RotateCcw size={11} /> Reset to base defaults
          </button>
          <div className="ml-auto flex gap-3 text-[11px]">
            <span><strong className="text-emerald-700">{counts.edit}</strong> edit</span>
            <span><strong className="text-sky-700">{counts.view}</strong> view</span>
            <span><strong className="text-rose-700">{counts.hidden}</strong> hidden</span>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 pr-1" data-testid="perm-matrix">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
          ) : (
            <>
              <div className="mt-2 border border-border rounded-sm p-3 bg-secondary/20">
                <div className="meta-label mb-2">Page Access</div>
                <p className="text-xs text-muted-foreground mb-3">Tick which pages users with this role can see in the sidebar &amp; open. <strong>My Leads</strong> is always available.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {PAGES_LIST.map((p) => (
                    <label key={p.key} className="inline-flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/40 px-2 py-1 rounded-sm" data-testid={`page-perm-${p.key}-label`}>
                      <input
                        type="checkbox"
                        checked={!!pagePerms[p.key]}
                        onChange={(e) => setPagePerms((prev) => ({ ...prev, [p.key]: e.target.checked }))}
                        data-testid={`page-perm-${p.key}`}
                        className="accent-primary"
                      />
                      <span>{p.label}</span>
                    </label>
                  ))}
                </div>

                {/* Admin Panel — multi-select dropdown of sub-pages. A role can be
                   granted access to specific Admin tabs (e.g., a Team Lead may
                   only need Campaigns + Shifts) instead of the full Admin Panel.
                   Selecting any sub-page grants the Admin sidebar link; selecting
                   ALL sub-pages is equivalent to full admin tab access. */}
                <div className="mt-4 pt-3 border-t border-border/60">
                  <div className="meta-label mb-2">Admin Panel</div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Pick the Admin tabs this role can open. Leave empty to hide the Admin link entirely.
                  </p>
                  <MultiSelectDropdown
                    testid="admin-tabs-multi"
                    placeholder="Select Admin tabs…"
                    options={ADMIN_TAB_OPTIONS}
                    value={ADMIN_TAB_OPTIONS.filter((o) => pagePerms[o.value] === true).map((o) => o.value)}
                    onChange={(selected) => {
                      setPagePerms((prev) => {
                        const next = { ...prev };
                        ADMIN_TAB_OPTIONS.forEach((o) => {
                          next[o.value] = selected.includes(o.value);
                        });
                        return next;
                      });
                    }}
                  />
                </div>

                {/* Per-role feature flags. Today only `export_leads` lives
                    here but the section is generic so future capabilities
                    (download CSV, bulk delete, etc.) can drop in. */}
                <div className="mt-4 pt-3 border-t border-border/60">
                  <div className="meta-label mb-2">Feature Permissions</div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Optional capabilities. Independent from page access — the role still needs the underlying page to use these.
                  </p>
                  <div className="space-y-1.5">
                    {FEATURE_FLAGS.map((f) => (
                      <label
                        key={f.key}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary/40 px-1 py-1 rounded-sm"
                        data-testid={`feature-${f.key}-row`}
                      >
                        <input
                          type="checkbox"
                          checked={!!pagePerms[f.key]}
                          onChange={(e) => setPagePerms((prev) => ({ ...prev, [f.key]: e.target.checked }))}
                          className="h-3.5 w-3.5"
                          data-testid={`feature-${f.key}-checkbox`}
                        />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="meta-label mt-5 mb-1">Field-level access on the Lead Form</div>
              {sections.map((s) => (
              <div key={s.title} className="mt-4">
                <div className="meta-label sticky top-0 bg-background py-1 z-10 border-b border-border">{s.title}</div>
                <table className="w-full text-sm">
                  <tbody>
                    {s.fields.map((f) => {
                      const cur = perms[f.name] || defaults[f.name] || "edit";
                      return (
                        <tr key={f.name} className="border-b border-border last:border-0 hover:bg-secondary/30">
                          <td className="py-1.5 pr-2 w-1/3">
                            <div className="text-xs font-medium">{f.label}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">{f.name}</div>
                          </td>
                          <td className="py-1.5">
                            <div className="inline-flex gap-1">
                              {LEVELS.map((l) => (
                                <button
                                  key={l.value}
                                  type="button"
                                  onClick={() => setPerms((p) => ({ ...p, [f.name]: l.value }))}
                                  data-testid={`perm-${f.name}-${l.value}`}
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[11px] ${cur === l.value ? `${TONE_FOR[l.value]} font-medium` : "border-border text-muted-foreground hover:bg-secondary"}`}
                                >
                                  <l.icon size={10} /> {l.label}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
            </>
          )}
        </div>

        {/* History drawer — slides over the right side of the dialog with a
            list of prior snapshots and a Restore button per row. Restore is
            itself reversible (the current state is snapshotted first). */}
        {historyOpen && (
          <div
            className="absolute inset-0 bg-black/40 flex justify-end z-20"
            data-testid="perm-history-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setHistoryOpen(false); }}
          >
            <div className="bg-background border-l border-border w-full max-w-md h-full overflow-y-auto p-4 shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold">Permission history</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{role.name}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setHistoryOpen(false)}
                  className="text-muted-foreground hover:text-foreground text-xs"
                  data-testid="perm-history-close"
                >
                  Close
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
                Every save snapshots the previous state here. Click <strong>Restore</strong> to roll back — the current state is snapshotted first, so restore is reversible.
              </p>
              {historyLoading ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  <Loader2 className="inline animate-spin mr-2" size={14} /> Loading history…
                </div>
              ) : history.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  No previous snapshots for this role yet. Snapshots are recorded automatically on every save.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="perm-history-list">
                  {history.map((snap) => {
                    const ts = new Date(snap.ts).toLocaleString();
                    const isRestoring = restoringId === snap.id;
                    const tone = snap.kind === "page"
                      ? "border-indigo-200 bg-indigo-50/40"
                      : "border-amber-200 bg-amber-50/40";
                    return (
                      <li
                        key={snap.id}
                        className={`border rounded-sm p-2.5 ${tone}`}
                        data-testid={`perm-history-item-${snap.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] font-mono text-muted-foreground">{ts}</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${snap.kind === "page" ? "border-indigo-300 text-indigo-700" : "border-amber-300 text-amber-700"}`}>
                            {snap.kind === "page" ? "Page access" : "Field permissions"}
                          </span>
                        </div>
                        <div className="text-xs mt-1.5 flex items-center justify-between">
                          <div>
                            <span className="text-muted-foreground">{snap.before_count}</span>
                            <span className="text-muted-foreground mx-1">→</span>
                            <span className="font-medium">{snap.after_count}</span>
                            <span className="text-muted-foreground ml-1 text-[10px]">keys</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[55%]" title={snap.who_email}>
                            by {snap.who_email || "system"}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isRestoring}
                            onClick={() => restoreSnapshot(snap)}
                            data-testid={`perm-history-restore-${snap.id}`}
                            className="rounded-sm h-7 text-[11px]"
                          >
                            {isRestoring ? <Loader2 className="animate-spin mr-1" size={11} /> : <RotateCcw size={11} className="mr-1" />}
                            Restore this snapshot
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-border pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            className="rounded-sm mr-auto"
            data-testid="perm-history-open"
          >
            <History size={14} className="mr-1" /> History
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="perm-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="rounded-sm bg-primary hover:bg-[#00227A]" data-testid="perm-save">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Save permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
